import { useQuery } from "@tanstack/react-query";
import { runsOnDevice } from "../api/client.js";
import { normalize, scoreMatch } from "../search/useUniversalSearch.js";
import type { SubscriptionChannel } from "./socialRows.js";

/**
 * "From YouTube" in the app's Search (build guide docs/social-connect-youtube.md, Phase 2e).
 *
 *  - As you type: the profile's own subscriptions that match, read from the device's cache
 *    (nothing is asked of any server for the names; the matched channels' latest videos come
 *    from the Social route the Social page already uses).
 *  - On submit (Done / Enter / the "Search all of YouTube" row): all of YouTube, through our
 *    server route, which keeps the key, the daily quota and a 24-hour cache. One call per
 *    submitted search, never per keystroke.
 *  - Kids profiles: none of it.
 */

/** One result, as the server answers (services/api/src/social-youtube.ts SocialSearchResult). */
export interface YoutubeSearchResult {
  videoId: string;
  title: string;
  channelId: string;
  channelTitle: string;
  thumbnail: string;
  publishedAt: string;
  live: boolean;
}

export interface YoutubeSearchAnswer {
  results: YoutubeSearchResult[];
  /** The day's search allowance is used up (or the key is missing): no results, said quietly. */
  quotaLimited: boolean;
  liveChecked: boolean;
}

const SERVER = "https://sliquetv.online";
/** The server refuses shorter or longer (services/api/src/social-youtube.ts). */
export const SEARCH_MIN = 2;
export const SEARCH_MAX = 80;

export function searchUrl(term: string): string {
  const path = `/api/social/youtube/search?q=${encodeURIComponent(term.trim().replace(/\s+/g, " "))}`;
  return runsOnDevice() ? SERVER + path : path;
}

/** The submitted term in the form the server accepts, or null when it cannot be searched. */
export function searchableTerm(term: string): string | null {
  const clean = term.trim().replace(/\s+/g, " ");
  return clean.length >= SEARCH_MIN && clean.length <= SEARCH_MAX ? clean : null;
}

export function useYoutubeSearch(term: string | null) {
  return useQuery({
    queryKey: ["social", "youtube-search", term?.toLowerCase() ?? ""],
    queryFn: async (): Promise<YoutubeSearchAnswer> => {
      const response = await fetch(searchUrl(term!));
      if (!response.ok) throw new Error(`YouTube search ${response.status}`);
      return (await response.json()) as YoutubeSearchAnswer;
    },
    enabled: term !== null,
    // The server holds an answer a day and the live flags it checks fresh; ten minutes here keeps
    // going back to the same search free without a stale LIVE tag lingering.
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
}

/** How many subscription matches are offered above the YouTube results. */
export const MAX_SUBSCRIPTION_MATCHES = 5;

/** The subscribed channels whose name matches what is typed, best first. Pure. */
export function matchSubscriptions(
  channels: readonly SubscriptionChannel[] | null | undefined,
  query: string,
  cap = MAX_SUBSCRIPTION_MATCHES,
): SubscriptionChannel[] {
  const needle = normalize(query);
  if (!channels || needle.length < SEARCH_MIN) return [];
  return channels
    .map((channel) => ({ channel, score: scoreMatch(normalize(channel.title), needle) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.channel.title.localeCompare(b.channel.title))
    .slice(0, cap)
    .map((entry) => entry.channel);
}
